/**
 * Settings → Channels.
 *
 * The dealer-facing surface for connecting WhatsApp, SMS and voice. Two ways in:
 *
 *   1. WhatsApp by QR       — pair the dealer's own WhatsApp from their phone,
 *                             via spectraiq-wa-gateway (Baileys).
 *   2. Twilio account       — paste credentials, sync the numbers on the
 *                             account, then decide per number whether it carries
 *                             SMS, WhatsApp, and how inbound calls are handled.
 *
 * WhatsApp is an either/or: a dealership runs it through ONE provider. WhatsApp is
 * two-way, and a reply has to leave from the same identity the customer messaged —
 * so running both would answer a QR-account conversation from a Twilio sender.
 * The panel therefore renders a two-option picker, and switching is a confirmed,
 * destructive act.
 *
 * Data: one polled GET /v1/channels/status drives the whole panel (matching the
 * portal's existing setInterval-polling convention — there is no WS/SSE here).
 */
import { useCallback, useEffect, useState } from 'react';
import { formatDate, formatInOrgTz } from '../../lib/datetime';
import { channelsService } from '../../lib/services';
import WhatsAppConfigureModal from '../WhatsAppConfigureModal';

/* ────────────────────────────── shared bits ────────────────────────────── */

const input = {
  width: '100%',
  padding: '7px 10px',
  fontSize: 12,
  fontFamily: 'var(--font-mono)',
  color: 'var(--text-primary)',
  background: 'var(--bg-glass)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-sm)',
  outline: 'none',
};

const label = { fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
const row = { padding: '12px 0', borderBottom: '1px solid var(--border-subtle)' };

function Field({ title, hint, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={label}>{title}</label>
      {children}
      {hint && <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    ACTIVE:          ['Connected',      'success'],
    PENDING:         ['Not verified',   'warning'],
    INVALID:         ['Rejected',       'danger'],
    DISABLED:        ['Disabled',       ''],
    NOT_CONFIGURED:  ['Not connected',  ''],
    CONNECTED:       ['Connected',      'success'],
    PAIRING:         ['Pairing',        'warning'],
    DISCONNECTED:    ['Not connected',  ''],
    ERROR:           ['Error',          'danger'],
    BANNED:          ['Banned',         'danger'],
    PROVISIONED:     ['Webhooks set',   'success'],
    STALE:           ['Webhooks stale', 'warning'],
    NOT_PROVISIONED: ['No webhooks',    ''],
  };
  const [text, tone] = map[status] || [status, ''];
  return <span className={`pill ${tone} ${tone ? 'filled' : ''}`}>{text}</span>;
}

/* ────────────────────────────── Twilio card ────────────────────────────── */

/**
 * Credentials form when disconnected; masked summary + test/disconnect when
 * connected. A save that Twilio rejects still returns 200 (the row was stored,
 * Twilio just refused it), so we branch on `status`, not on the promise.
 */
function TwilioCard({ twilio, webhookUrls, onChanged }) {
  const [form, setForm] = useState({
    accountSid: '', authToken: '', apiKeySid: '', apiKeySecret: '',
    region: 'us1', edge: '', mode: 'live', webhookBaseUrl: '',
  });
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote]   = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true); setError(null); setNote(null);
    try {
      const saved = await channelsService.saveTwilio(form);
      if (saved?.status === 'INVALID') {
        setError(saved.lastError || 'Twilio rejected these credentials.');
      } else {
        setNote(`Connected to ${saved?.friendlyName || 'Twilio'}.`);
        setForm((f) => ({ ...f, authToken: '', apiKeySecret: '' }));  // don't keep secrets in component state
      }
      onChanged();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not save credentials.');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true); setError(null); setNote(null);
    try {
      const r = await channelsService.testTwilio();
      if (r?.ok) setNote(`Twilio OK — ${r.friendlyName} · ${r.numberCount} number(s).`);
      else setError(r?.error || 'Twilio rejected the stored credentials.');
      onChanged();
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Remove Twilio credentials and all synced numbers? Inbound messages and calls will stop reaching SpectraIQ.')) return;
    setBusy(true);
    try { await channelsService.disconnectTwilio(); onChanged(); }
    finally { setBusy(false); }
  };

  if (twilio?.configured) {
    return (
      <div className="card animate-in">
        <div className="card-label">TWILIO ACCOUNT</div>

        <div className="flex-row" style={row}>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
            {twilio.friendlyName || 'Twilio'}
          </span>
          <StatusPill status={twilio.status} />
        </div>

        <div className="flex-between" style={{ ...row, fontSize: 12 }}>
          <span className="text-muted">Account SID</span>
          <span className="mono text-xs">{twilio.accountSidMasked}</span>
        </div>
        <div className="flex-between" style={{ ...row, fontSize: 12 }}>
          <span className="text-muted">Auth</span>
          <span className="mono text-xs">{twilio.hasApiKey ? 'API key' : 'Account auth token'}</span>
        </div>
        <div className="flex-between" style={{ ...row, fontSize: 12 }}>
          <span className="text-muted">Region</span>
          <span className="mono text-xs">
            {{ us1: 'United States (us1)', au1: 'Australia (au1)', ie1: 'Ireland (ie1)' }[twilio.region] || twilio.region || 'us1'}
          </span>
        </div>
        <div className="flex-between" style={{ ...row, fontSize: 12 }}>
          <span className="text-muted">Mode</span>
          <span className="mono text-xs">{twilio.mode === 'stub' ? 'Test mode — not sending' : 'Live'}</span>
        </div>

        {twilio.lastError && (
          <div className="mono text-xs text-danger" style={{ marginTop: 10, lineHeight: 1.5 }}>
            {twilio.lastError}
          </div>
        )}
        {error && <div className="mono text-xs text-danger" style={{ marginTop: 10 }}>{error}</div>}
        {note  && <div className="mono text-xs text-success" style={{ marginTop: 10 }}>{note}</div>}

        {/* Dealers who'd rather paste URLs into Twilio themselves can copy them
            from here instead of using the per-number Provision button. */}
        {webhookUrls?.sms && (
          <details style={{ marginTop: 12 }}>
            <summary className="mono text-xs text-muted" style={{ cursor: 'pointer' }}>
              Webhook URLs (set automatically by “Provision”)
            </summary>
            <div className="mono text-xs text-muted" style={{ marginTop: 8, lineHeight: 1.8, wordBreak: 'break-all' }}>
              <div>SMS · {webhookUrls.sms}</div>
              <div>WhatsApp · {webhookUrls.whatsapp}</div>
              <div>Voice · {webhookUrls.voice}</div>
              <div>Call status · {webhookUrls.voiceStatus}</div>
            </div>
          </details>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="btn small danger" onClick={disconnect} disabled={busy}>Disconnect</button>
          <button className="btn small" onClick={test} disabled={busy}>
            {busy ? 'Testing…' : 'Test connection'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card animate-in">
      <div className="card-label">CONNECT TWILIO</div>
      <div className="mono text-xs text-muted" style={{ marginBottom: 14, lineHeight: 1.6 }}>
        Paste the credentials from console.twilio.com. Once connected you can sync
        this account’s numbers and choose what each one does — SMS, WhatsApp, and
        how inbound calls are answered.
      </div>

      <Field title="Account SID" hint="Starts with AC — found on the Twilio console dashboard.">
        <input style={input} value={form.accountSid} onChange={set('accountSid')}
               placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" autoComplete="off" />
      </Field>

      <Field
        title="Auth Token"
        hint={form.region && form.region !== 'us1'
          ? `⚠ Each Twilio region has its OWN Auth Token. The Console dashboard shows the us1 one — that will NOT work here. Get the ${form.region} token from Account → API keys & tokens, with the Region selector set to ${form.region === 'au1' ? 'Australia' : 'Ireland'}.`
          : 'Stored encrypted (AES-256-GCM). Never shown again after saving.'}
      >
        <input style={input} type="password" value={form.authToken} onChange={set('authToken')}
               placeholder="••••••••••••••••••••••••••••••••" autoComplete="new-password" />
      </Field>

      {/* Region is NOT hidden under "advanced" on purpose. Twilio runs separate
          regional stacks and a regional account's credentials exist only on that
          region's servers — so choosing the wrong one returns "Authenticate
          (20003)", which looks exactly like a mistyped token. People then re-copy a
          token that was correct all along. Make the choice unmissable. */}
      <Field
        title="Twilio Region"
        hint="Where your Twilio account lives. Choosing this is not enough on its own — each region also has its OWN Auth Token, so you must paste the token from that region (see below). Twilio reports a region/token mismatch as a plain authentication error, which is why it looks like a bad token."
      >
        <select style={input} value={form.region} onChange={set('region')}>
          <option value="us1">United States (us1) — default</option>
          <option value="au1">Australia (au1)</option>
          <option value="ie1">Ireland (ie1)</option>
        </select>
      </Field>

      <button className="btn small" style={{ marginBottom: 12 }}
              onClick={() => setShowAdvanced((v) => !v)}>
        {showAdvanced ? 'Hide' : 'Show'} advanced
      </button>

      {showAdvanced && (
        <>
          <Field
            title="API Key SID (optional)"
            hint="Preferred for API calls: an API key can be revoked without rotating your account auth token, which would break signature validation on live webhooks."
          >
            <input style={input} value={form.apiKeySid} onChange={set('apiKeySid')}
                   placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" autoComplete="off" />
          </Field>
          <Field title="API Key Secret (optional)">
            <input style={input} type="password" value={form.apiKeySecret} onChange={set('apiKeySecret')}
                   autoComplete="new-password" />
          </Field>
          <Field
            title="Public webhook base URL (optional)"
            hint="Where Twilio calls us back, e.g. https://api.yourdealership.com. Must be publicly reachable over HTTPS. Falls back to the server default."
          >
            <input style={input} value={form.webhookBaseUrl} onChange={set('webhookBaseUrl')}
                   placeholder="https://api.yourdealership.com" />
          </Field>
          <Field title="Mode" hint="Test mode records messages without sending them to customers — useful for trying things out without spending credit.">
            <select style={input} value={form.mode} onChange={set('mode')}>
              <option value="live">Live — send to customers</option>
              <option value="stub">Test mode — don't send (record only)</option>
            </select>
          </Field>
        </>
      )}

      {error && <div className="mono text-xs text-danger" style={{ marginTop: 4, marginBottom: 8, lineHeight: 1.5 }}>{error}</div>}
      {note  && <div className="mono text-xs text-success" style={{ marginTop: 4, marginBottom: 8 }}>{note}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
        <button className="btn small primary" onClick={save}
                disabled={busy || !form.accountSid || !form.authToken}>
          {busy ? 'Verifying…' : 'Connect Twilio'}
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────── Numbers ────────────────────────────── */

/**
 * One row per Twilio number, with what it's allowed to do.
 *
 * The capability checks here mirror the server's — a number Twilio says is
 * SMS-only shouldn't offer a voice dropdown. The server re-checks anyway; this
 * is for clarity, not security.
 */
/**
 * One channel's webhook state, with the ability to say "I did this in Twilio myself".
 *
 * Manual marking is a first-class path, not an escape hatch:
 *   - a WhatsApp SENDER has no IncomingPhoneNumber, so there is nothing for us to
 *     provision — marking it is the ONLY way to record that it's wired up;
 *   - a number inside a Messaging Service takes its webhook from the SERVICE, which
 *     overrides the number's, so auto-provisioning would write a URL Twilio ignores
 *     and then report success.
 */
function WebhookChip({ channel, state, manual, disabled, busy, onMark }) {
  const META = {
    AUTO:   ['Auto',   'success', 'SpectraIQ set this webhook in Twilio and Twilio confirms it.'],
    MANUAL: ['Manual', 'success', 'You marked this as configured by hand in Twilio.'],
    STALE:  ['Stale',  'warning', 'Twilio has one of our URLs, but from a different base URL.'],
    ERROR:  ['Error',  'danger',  'The last provisioning attempt failed.'],
    NONE:   ['Not set', '',       'No webhook. Inbound on this channel will not reach SpectraIQ.'],
  };
  const [text, tone, title] = META[state] || [state, '', ''];

  return (
    <div className="flex-row" style={{ gap: 5, alignItems: 'center', opacity: disabled ? 0.4 : 1 }}>
      <span className="text-xs text-muted" style={{ width: 58 }}>{channel}</span>
      <span className={`pill ${tone} ${tone ? 'filled' : ''}`} title={title}>{text}</span>
      <label
        className="text-xs text-muted"
        title="Tick if you set this webhook yourself in the Twilio console"
        style={{ display: 'flex', alignItems: 'center', gap: 3,
                 cursor: disabled || busy ? 'not-allowed' : 'pointer' }}
      >
        <input
          type="checkbox"
          checked={manual}
          disabled={disabled || busy}
          onChange={(e) => onMark(channel.toUpperCase(), e.target.checked)}
        />
        set in Twilio
      </label>
    </div>
  );
}

function NumberRow({ n, onChanged }) {
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [forward, setForward] = useState(n.forwardToNumber || '');

  const caps = n.capabilities || {};

  // "We put the webhooks there" — the only state in which Unprovision is meaningful.
  // STALE counts: it's still our URL, just from an older base URL, and it should be
  // removable. MANUAL does not: that webhook was never ours to take away.
  const provisionedByUs =
    n.webhookStatus === 'PROVISIONED' || n.webhookStatus === 'STALE';

  const patch = async (body) => {
    setBusy(true); setError(null);
    try { await channelsService.updateNumber(n.id, body); onChanged(); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  const markManual = async (channel, manual) => {
    setBusy(true); setError(null);
    try { await channelsService.markWebhookManual(n.id, channel, manual); onChanged(); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  const provision = async () => {
    setBusy(true); setError(null);
    try { await channelsService.provisionWebhooks(n.id); onChanged(); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  const unprovision = async () => {
    if (!window.confirm(
      `Remove SpectraIQ's webhooks from ${n.phoneNumber} in Twilio?\n\n`
      + 'Messages and calls to this number will stop reaching SpectraIQ immediately. '
      + 'Webhooks that don\u2019t belong to SpectraIQ are left untouched.'
    )) return;
    setBusy(true); setError(null);
    try { await channelsService.unprovisionWebhooks(n.id); onChanged(); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  const setVoiceMode = (mode) => {
    // FORWARD without a destination is rejected by both a DB CHECK and the
    // service layer — don't fire a request we know will 400.
    if (mode === 'FORWARD' && !forward.trim()) {
      patch({ voiceMode: 'FORWARD', forwardToNumber: forward.trim() || null })
        .catch(() => {});
      setError('Enter a number to forward to.');
      return;
    }
    patch({ voiceMode: mode, forwardToNumber: mode === 'FORWARD' ? forward.trim() : null });
  };

  return (
    <div style={{ ...row, opacity: busy ? 0.6 : 1 }}>
      <div className="flex-row" style={{ alignItems: 'center', gap: 8 }}>
        <span className="mono" style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
          {n.phoneNumber}
        </span>
        {/* Auto-provision only works on a number we own (it PATCHes the
            IncomingPhoneNumber). A WhatsApp-only sender has none, so the button
            would always fail — don't offer it. */}
        {n.twilioSid && (
          <>
            <button className="btn small" onClick={provision} disabled={busy}
                    title="Write SpectraIQ's SMS + Voice webhook URLs onto this number in Twilio">
              {provisionedByUs ? 'Re-provision' : 'Provision'}
            </button>
            {/* Only offered when SpectraIQ actually put the webhooks there. Showing
                it otherwise would invite someone to "unprovision" a number whose
                webhooks belong to a different system — the backend refuses that, but
                the button shouldn't tempt them in the first place. */}
            {provisionedByUs && (
              <button className="btn small danger" onClick={unprovision} disabled={busy}
                      title="Remove SpectraIQ's webhooks from this number in Twilio">
                Unprovision
              </button>
            )}
          </>
        )}
      </div>

      {/* Per-channel webhook state. Three, not one — SMS, Voice and WhatsApp are
          three separate settings in three different places in Twilio. */}
      <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
        <WebhookChip
          channel="SMS"
          state={n.smsWebhook}
          manual={n.smsWebhookManual}
          disabled={!caps.sms}
          busy={busy}
          onMark={markManual}
        />
        <WebhookChip
          channel="Voice"
          state={n.voiceWebhook}
          manual={n.voiceWebhookManual}
          disabled={!caps.voice}
          busy={busy}
          onMark={markManual}
        />
        <WebhookChip
          channel="WhatsApp"
          state={n.whatsappWebhook}
          manual={n.whatsappWebhookManual}
          busy={busy}
          onMark={markManual}
        />
      </div>

      {/* A WhatsApp sender can only ever be manual — say so, rather than leaving
          someone hunting for a Provision button that cannot exist. */}
      {!n.twilioSid && (
        <div className="mono text-xs text-muted" style={{ marginTop: 6, lineHeight: 1.5 }}>
          This is a WhatsApp sender, not a number you own — Twilio has no phone-number
          resource for us to configure. Set its webhook in the Twilio console
          (Messaging → Senders), then tick “set in Twilio” above.
        </div>
      )}

      {/* The dealer said it's done, but Twilio still shows nothing on the number.
          Usually right (the webhook is on a Messaging Service, which we can't read),
          occasionally the tick was optimistic. Say both. */}
      {(n.smsWebhookManual || n.voiceWebhookManual) && n.twilioSid
        && n.webhookStatus === 'NOT_PROVISIONED' && (
        <div className="mono text-xs text-warning" style={{ marginTop: 6, lineHeight: 1.5 }}>
          Marked as set manually, but Twilio reports no webhook URL on this number.
          That's expected if you configured it on a Messaging Service (which overrides
          the number's own webhook, and which we can't read). Otherwise, double-check it.
        </div>
      )}

      {n.webhookManualBy && (
        <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
          Marked manual by {n.webhookManualBy}
          {n.webhookManualAt ? ` · ${formatDate(n.webhookManualAt)}` : ''}
          {n.webhookManualNote ? ` · ${n.webhookManualNote}` : ''}
        </div>
      )}

      {n.friendlyName && n.friendlyName !== n.phoneNumber && (
        <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>{n.friendlyName}</div>
      )}

      <div className="flex-row" style={{ gap: 16, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <label className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: 6,
                 opacity: caps.sms ? 1 : 0.4, cursor: caps.sms ? 'pointer' : 'not-allowed' }}>
          <input type="checkbox" checked={n.smsEnabled} disabled={!caps.sms || busy}
                 onChange={(e) => patch({ smsEnabled: e.target.checked })} />
          SMS {!caps.sms && <span className="text-xs text-muted">(not supported)</span>}
        </label>

        {/* WhatsApp is not a per-number toggle. A number carries WhatsApp because it
            *is* the dealership's WhatsApp sender, which is set by choosing Twilio as
            the provider below — flipping it on here would leave inbound messages
            arriving with no connection to reply from. Shown read-only for clarity. */}
        {n.whatsappEnabled && (
          <span className="pill success filled" title="This number is your WhatsApp sender">
            WhatsApp sender
          </span>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                      opacity: caps.voice ? 1 : 0.4 }}>
          <span className="text-sm">Calls</span>
          <select
            style={{ ...input, width: 'auto', padding: '4px 8px' }}
            value={n.voiceMode}
            disabled={!caps.voice || busy}
            onChange={(e) => setVoiceMode(e.target.value)}
          >
            <option value="DISABLED">Don’t answer</option>
            <option value="AI_AGENT">AI voice agent</option>
            <option value="FORWARD">Forward to a person</option>
          </select>
        </div>

        {n.voiceMode === 'FORWARD' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              style={{ ...input, width: 150, padding: '4px 8px' }}
              value={forward}
              placeholder="+61400111222"
              onChange={(e) => setForward(e.target.value)}
              onBlur={() => {
                if (forward.trim() && forward.trim() !== n.forwardToNumber) {
                  patch({ forwardToNumber: forward.trim() });
                }
              }}
            />
            <label className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={n.recordCalls} disabled={busy}
                     onChange={(e) => patch({ recordCalls: e.target.checked })} />
              Record
            </label>
          </div>
        )}
      </div>

      {/* Which number outbound leaves from. Only worth showing once more than
          one number can carry the channel. */}
      <div className="flex-row" style={{ gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        {n.smsEnabled && (
          <label className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="radio" name="defaultSms" checked={n.defaultSms} disabled={busy}
                   onChange={() => patch({ defaultSms: true })} />
            Default SMS sender
          </label>
        )}
        {n.voiceMode !== 'DISABLED' && (
          <label className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="radio" name="defaultVoice" checked={n.defaultVoice} disabled={busy}
                   onChange={() => patch({ defaultVoice: true })} />
            Default caller ID
          </label>
        )}
      </div>

      {(error || n.lastError) && (
        <div className="mono text-xs text-danger" style={{ marginTop: 8, lineHeight: 1.5 }}>
          {error || n.lastError}
        </div>
      )}
    </div>
  );
}

function NumbersCard({ numbers, twilio, onChanged }) {
  const [syncing, setSyncing] = useState(false);
  const [error, setError]     = useState(null);

  const sync = async () => {
    setSyncing(true); setError(null);
    try { await channelsService.syncNumbers(); onChanged(); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setSyncing(false); }
  };

  if (!twilio?.configured) return null;

  return (
    <div className="card animate-in" style={{ animationDelay: '0.05s' }}>
      <div className="flex-between" style={{ marginBottom: 10 }}>
        <div className="card-label" style={{ marginBottom: 0 }}>PHONE NUMBERS</div>
        <button className="btn small" onClick={sync} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync from Twilio'}
        </button>
      </div>

      {error && <div className="mono text-xs text-danger" style={{ marginBottom: 10 }}>{error}</div>}

      {(!numbers || numbers.length === 0) ? (
        <div className="mono text-xs text-muted" style={{ padding: '16px 0', lineHeight: 1.6 }}>
          No numbers yet. Click <strong>Sync from Twilio</strong> to pull in the numbers on
          this account, then choose what each one does.
        </div>
      ) : (
        <>
          {numbers.map((n) => <NumberRow key={n.id} n={n} onChanged={onChanged} />)}
          <div className="mono text-xs text-muted" style={{ marginTop: 12, lineHeight: 1.6 }}>
            <strong>Provision</strong> points the number’s Twilio webhooks at SpectraIQ, so its
            messages and calls reach us. Without it, inbound goes nowhere.
          </div>
        </>
      )}
    </div>
  );
}

/* ────────────────────────────── WhatsApp ────────────────────────────── */

/**
 * WhatsApp is an EITHER/OR choice — QR pairing or a Twilio sender, never both.
 *
 * Why the UI makes this exclusive rather than offering both: WhatsApp is two-way.
 * Every inbound message gets an AI reply, and the reply has to leave from the same
 * WhatsApp identity the customer messaged. If both providers were live, a customer
 * who messaged the QR-paired number would be answered from the Twilio sender — a
 * different number, a split thread, and on Twilio's side an un-templated message
 * outside the 24h window that would be rejected anyway.
 *
 * So this renders as a two-option picker, and switching is a deliberate,
 * confirmed, destructive act.
 */

const PROVIDERS = {
  QR: {
    id: 'QR',
    name: 'Scan a QR code',
    tagline: 'Use the WhatsApp number you already have',
    good: [
      'Free — no Twilio account, no per-message cost',
      'Live in about a minute: scan and go',
      'Keeps the number your customers already know',
    ],
    bad: [
      'Unofficial client — WhatsApp may ban the number',
      'Not suitable for bulk or campaign sending',
      'Breaks if the phone is offline for too long',
    ],
    best: 'Best for piloting, and for dealers whose WhatsApp volume is conversational rather than campaign-driven.',
  },
  TWILIO: {
    id: 'TWILIO',
    name: 'Twilio WhatsApp Business',
    tagline: 'Official WhatsApp Business API, via Twilio',
    good: [
      'Sanctioned by Meta — won’t get banned for automation',
      'Message templates for campaigns and reminders',
      'Higher throughput and delivery reporting',
    ],
    bad: [
      'Per-message cost, billed by Twilio',
      'Sender registration is reviewed by Meta (hours to days)',
      'A new business number — not your existing handset',
    ],
    best: 'Best for production: lost-lead campaigns, service reminders, anything at volume.',
  },
};

/** One selectable provider card. */
function ProviderOption({ p, active, current, disabled, onChoose }) {
  const isCurrent = current === p.id;
  return (
    <div
      className="card"
      style={{
        padding: 14,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-color)'}`,
        background: active ? 'var(--accent-glow)' : 'var(--bg-card)',
        transition: 'all var(--transition-fast)',
      }}
      onClick={() => !disabled && onChoose(p.id)}
    >
      <div className="flex-between" style={{ marginBottom: 6 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</span>
        {isCurrent
          ? <span className="pill success filled">In use</span>
          : <input type="radio" checked={active} readOnly />}
      </div>
      <div className="mono text-xs text-muted" style={{ marginBottom: 10 }}>{p.tagline}</div>

      <div style={{ display: 'grid', gap: 3, marginBottom: 8 }}>
        {p.good.map((g, i) => (
          <div key={i} className="text-xs" style={{ color: 'var(--success)' }}>+ {g}</div>
        ))}
        {p.bad.map((b, i) => (
          <div key={i} className="text-xs text-muted">− {b}</div>
        ))}
      </div>

      <div className="mono text-xs text-muted" style={{ lineHeight: 1.5, fontStyle: 'italic' }}>
        {p.best}
      </div>
    </div>
  );
}

/**
 * Switching provider is destructive and irreversible-ish (a wiped QR pairing has
 * to be re-scanned from the phone), so it gets a real confirmation that names the
 * specific consequences rather than a generic "are you sure".
 */
function SwitchConfirm({ from, to, onCancel, onConfirm, busy }) {
  const losing = PROVIDERS[from];
  const gaining = PROVIDERS[to];
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div className="card" style={{ width: 460, padding: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
          Switch WhatsApp to {gaining.name}?
        </div>
        <div className="text-sm" style={{ lineHeight: 1.6, color: 'var(--text-secondary)', marginBottom: 12 }}>
          A dealership can only run WhatsApp through one provider — replies have to
          come from the same number your customer messaged. Switching will:
        </div>
        <ul className="text-sm" style={{ lineHeight: 1.8, paddingLeft: 18, marginBottom: 12 }}>
          <li>
            <strong>Disconnect {losing.name}.</strong>{' '}
            {from === 'QR'
              ? 'The paired session is wiped — you’ll need to scan a new QR code to go back.'
              : 'The Twilio sender stops receiving here. Remove its webhook in the Twilio console.'}
          </li>
          <li>Stop inbound messages on the old number immediately.</li>
          <li>Leave existing conversations in the CRM — history isn’t deleted.</li>
        </ul>
        <div className="mono text-xs text-muted" style={{ marginBottom: 16, lineHeight: 1.5 }}>
          In-flight replies on the old number may not deliver. Best done outside opening hours.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn small" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn small danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Switching…' : `Switch to ${gaining.name}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function WhatsAppCard({ whatsapp, numbers, twilio, onChanged, onOpenQr }) {
  const connected = whatsapp?.connected;
  const current   = connected
    ? (whatsapp.provider === 'BAILEYS' ? 'QR' : 'TWILIO')
    : null;

  // What the user has clicked, before they commit to it.
  const [picked, setPicked]     = useState(current);
  const [sender, setSender]     = useState('');
  const [confirm, setConfirm]   = useState(null);   // provider id being switched TO
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);

  // Keep the selection in step when the poll brings a change from elsewhere.
  useEffect(() => { setPicked(current); }, [current]);

  const twilioReady = twilio?.configured && (numbers || []).length > 0;

  // Numbers Twilio says can carry WhatsApp. Populated by the sync, which reads
  // both the purchased numbers AND the Senders API — they are different resources
  // and a WhatsApp sender is frequently not a number you own.
  const waSenders = (numbers || []).filter((n) => n.capabilities?.whatsapp);

  const apply = async (provider) => {
    setBusy(true); setError(null);
    try {
      await channelsService.chooseWhatsAppProvider(
        provider,
        provider === 'TWILIO' ? sender.trim() : null);
      setConfirm(null);
      onChanged();
      // QR needs a scan to finish — open the pairing modal straight away.
      if (provider === 'QR') onOpenQr();
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const choose = (provider) => {
    setError(null);
    setPicked(provider);
    if (provider === current) return;                 // no change
    if (connected) { setConfirm(provider); return; }  // switching — confirm first
    if (provider === 'TWILIO') return;                // needs a sender picked below
    apply(provider);                                  // first-time QR: just go
  };

  const disconnect = async () => {
    if (!window.confirm(
      'Disconnect WhatsApp entirely? The dealership will stop sending and receiving WhatsApp messages.'
    )) return;
    setBusy(true);
    try { await channelsService.disconnectWhatsApp(); onChanged(); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="card animate-in" style={{ animationDelay: '0.1s' }}>
      <div className="flex-between" style={{ marginBottom: 4 }}>
        <div className="card-label" style={{ marginBottom: 0 }}>WHATSAPP</div>
        {connected && (
          <button className="btn small danger" onClick={disconnect} disabled={busy}>
            Disconnect
          </button>
        )}
      </div>
      <div className="mono text-xs text-muted" style={{ marginBottom: 14, lineHeight: 1.6 }}>
        Choose <strong>one</strong> way to run WhatsApp. Replies must come from the same
        number your customer messaged, so a dealership can’t run both at once.
      </div>

      {/* ── Current state banner ── */}
      {connected && (
        <div className="flex-row" style={{
          ...row, alignItems: 'center', gap: 8,
          borderBottom: '1px solid var(--border-subtle)', paddingBottom: 12, marginBottom: 12,
        }}>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
            {PROVIDERS[current]?.name}
            {whatsapp.phoneNumber && (
              <span className="mono text-xs text-muted" style={{ marginLeft: 8 }}>
                {whatsapp.phoneNumber}
              </span>
            )}
          </span>
          <StatusPill status={whatsapp.status} />
          {current === 'QR' && whatsapp.status !== 'CONNECTED' && (
            <button className="btn small primary" onClick={onOpenQr}>Scan QR</button>
          )}
        </div>
      )}

      {whatsapp?.status === 'BANNED' && (
        <div className="mono text-xs text-danger" style={{ marginBottom: 12, lineHeight: 1.6 }}>
          WhatsApp banned this number. QR pairing uses an unofficial client — for
          automated sending at any volume, switch to a Twilio sender.
        </div>
      )}

      {/* ── The choice ── */}
      <div className="grid-2" style={{ gap: 12 }}>
        <ProviderOption
          p={PROVIDERS.QR}
          active={picked === 'QR'}
          current={current}
          onChoose={choose}
        />
        <ProviderOption
          p={PROVIDERS.TWILIO}
          active={picked === 'TWILIO'}
          current={current}
          disabled={!twilioReady}
          onChoose={choose}
        />
      </div>

      {!twilioReady && (
        <div className="mono text-xs text-muted" style={{ marginTop: 10, lineHeight: 1.5 }}>
          Connect Twilio above and sync your numbers to enable the Twilio option.
        </div>
      )}

      {/* ── Twilio needs a sender before we can apply ──
           A WhatsApp sender is NOT the same Twilio resource as a purchased phone
           number: the sandbox number isn't owned by anyone, and a registered sender
           lives under Messaging → Senders. So we surface the WhatsApp-capable
           numbers the sync found, but always allow the number to be typed in — a
           sender we couldn't auto-discover must still be usable. ── */}
      {picked === 'TWILIO' && current !== 'TWILIO' && twilioReady && (
        <div style={{ marginTop: 12 }}>
          <Field
            title="Which number is your WhatsApp sender?"
            hint="It must already be registered as a WhatsApp sender in Twilio (Messaging → Senders) — Meta reviews that, and there’s no API for it."
          >
            <div className="flex-row" style={{ gap: 8 }}>
              <select
                style={{ ...input, flex: 1 }}
                value={waSenders.some((n) => n.phoneNumber === sender) ? sender : (sender ? '__manual__' : '')}
                onChange={(e) => setSender(e.target.value === '__manual__' ? ' ' : e.target.value)}
              >
                <option value="">Choose a sender…</option>
                {waSenders.map((n) => (
                  <option key={n.id} value={n.phoneNumber}>
                    {n.phoneNumber}
                    {n.capabilities?.whatsapp && !n.capabilities?.sms ? ' · WhatsApp sender' : ''}
                  </option>
                ))}
                <option value="__manual__">Enter a number manually…</option>
              </select>
              <button
                className="btn small primary"
                disabled={!sender?.trim() || busy}
                onClick={() => (connected ? setConfirm('TWILIO') : apply('TWILIO'))}
              >
                {busy ? 'Applying…' : 'Use this sender'}
              </button>
            </div>

            {/* Manual entry — the escape hatch for the sandbox number and for any
                sender the Senders API didn't return. */}
            {(sender === ' ' || (sender && !waSenders.some((n) => n.phoneNumber === sender))) && (
              <input
                style={{ ...input, marginTop: 8 }}
                value={sender.trim()}
                placeholder="+14155238886"
                onChange={(e) => setSender(e.target.value)}
              />
            )}
          </Field>

          {waSenders.length === 0 && (
            <div className="mono text-xs text-warning" style={{ marginTop: -4, marginBottom: 8, lineHeight: 1.6 }}>
              No WhatsApp senders were found on your Twilio account. If you have one,
              it may be the shared sandbox number (which nobody owns, so it can’t be
              discovered) — type it in above. Otherwise register a sender in Twilio
              under Messaging → Senders, then Sync again.
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mono text-xs text-danger" style={{ marginTop: 10, lineHeight: 1.5 }}>{error}</div>
      )}

      {confirm && (
        <SwitchConfirm
          from={current}
          to={confirm}
          busy={busy}
          onCancel={() => { setConfirm(null); setPicked(current); }}
          onConfirm={() => apply(confirm)}
        />
      )}
    </div>
  );
}

/* ────────────────────────────── Voice stream ────────────────────────────── */

/**
 * Voice stream health — one-click server-side probe of the same
 * wss://…/voice/twilio-media handshake Twilio performs on an AI_AGENT call.
 * An AI call that drops in ~2 seconds is almost always this handshake failing
 * (untrusted TLS, missing WebSocket proxy location, voice-loop down), and none
 * of that is visible from the Twilio call log.
 */
function VoiceStreamCard() {
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError]   = useState(null);

  const run = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      setResult(await channelsService.voiceHealth());
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Health check failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card animate-in" style={{ animationDelay: '0.12s' }}>
      <div className="card-label">VOICE STREAM</div>
      <div className="mono text-xs text-muted" style={{ lineHeight: 1.6, marginBottom: 10 }}>
        Tests the WebSocket handshake to the AI voice endpoint — the same connection
        Twilio opens when a call reaches an AI-answered number. Calls that drop
        within a couple of seconds usually mean this handshake is failing.
      </div>

      {result && (
        <div style={{ marginBottom: 10 }}>
          <div className="flex-between" style={{ ...row, fontSize: 12 }}>
            <span className="text-muted">Result</span>
            <span className={`mono text-xs ${result.ok ? 'text-success' : 'text-danger'}`}>
              {result.ok
                ? `OK · ${result.latencyMs} ms`
                : `FAILED at ${result.stage || '?'}${result.latencyMs != null ? ` · ${result.latencyMs} ms` : ''}`}
            </span>
          </div>
          <div className="flex-between" style={{ ...row, fontSize: 12 }}>
            <span className="text-muted">Endpoint</span>
            <span className="mono text-xs" style={{ wordBreak: 'break-all', textAlign: 'right', maxWidth: '70%' }}>
              {result.url || '(not configured)'}
            </span>
          </div>
          {result.error && (
            <div className="mono text-xs text-danger" style={{ marginTop: 8, lineHeight: 1.5 }}>
              {result.error}
            </div>
          )}
          {result.hint && (
            <div className="mono text-xs text-muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
              {result.hint}
            </div>
          )}
        </div>
      )}
      {error && <div className="mono text-xs text-danger" style={{ marginBottom: 10 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn small" onClick={run} disabled={busy}>
          {busy ? 'Checking…' : 'Run health check'}
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────── Activity ────────────────────────────── */

/** Recent calls — the "is this thing actually working?" panel. */
function ActivityCard({ events }) {
  if (!events || events.length === 0) return null;
  return (
    <div className="card animate-in" style={{ animationDelay: '0.15s' }}>
      <div className="card-label">RECENT CALLS</div>
      {events.map((e) => (
        <div key={e.id} className="flex-row" style={{ ...row, alignItems: 'center', gap: 8 }}>
          <span className="mono text-xs text-muted" style={{ width: 62 }}>
            {e.direction === 'INBOUND' ? '← in' : '→ out'}
          </span>
          <span className="mono" style={{ flex: 1, fontSize: 12 }}>{e.counterparty || '—'}</span>
          <span className="mono text-xs text-muted">{e.preview}</span>
          <span className="mono text-xs text-muted" style={{ width: 78, textAlign: 'right' }}>
            {e.status || '—'}
          </span>
          <span className="mono text-xs text-muted" style={{ width: 60, textAlign: 'right' }}>
            {formatInOrgTz(e.at, { hour: '2-digit', minute: '2-digit' }, '')}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────── Panel ────────────────────────────── */

export default function ChannelsPanel() {
  const [data, setData]         = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [qrOpen, setQrOpen]     = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [status, acts] = await Promise.all([
        channelsService.status(),
        channelsService.activity(10).catch(() => []),
      ]);
      setData(status);
      setActivity(Array.isArray(acts) ? acts : []);
    } catch {
      // Backend unreachable (or design-mode with no backend at all). Render the
      // disconnected state rather than an error page — the dealer's next action
      // is the same either way: connect something.
      setData((prev) => prev ?? { twilio: { configured: false, status: 'NOT_CONFIGURED' }, numbers: [], whatsapp: { connected: false } });
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling, matching the portal's existing convention (no WS/SSE anywhere yet).
  // 10s is brisk enough that a webhook provision or QR pair lands visibly, without
  // hammering the API on an idle Settings tab.
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [refresh]);

  const closeQr = useCallback(() => { setQrOpen(false); refresh(); }, [refresh]);

  if (loading && !data) {
    return <div className="card animate-in"><div className="mono text-xs text-muted">Loading channels…</div></div>;
  }

  const twilio      = data?.twilio || { configured: false, status: 'NOT_CONFIGURED' };
  const numbers     = data?.numbers || [];
  const whatsapp    = data?.whatsapp || { connected: false };
  const webhookUrls = data?.webhookUrls;

  return (
    <>
      <div className="flex-between" style={{ marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Channels</div>
          <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
            WhatsApp, SMS and voice — connect by QR, or through your Twilio account.
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 14 }}>
        <TwilioCard twilio={twilio} webhookUrls={webhookUrls} onChanged={refresh} />
        <NumbersCard numbers={numbers} twilio={twilio} onChanged={refresh} />
        <WhatsAppCard
          whatsapp={whatsapp}
          numbers={numbers}
          twilio={twilio}
          onChanged={refresh}
          onOpenQr={() => setQrOpen(true)}
        />
        <VoiceStreamCard />
        <ActivityCard events={activity} />
      </div>

      <WhatsAppConfigureModal
        open={qrOpen}
        onClose={closeQr}
        onStateChange={refresh}
      />
    </>
  );
}
